import React, { useState } from 'react';
import { Pencil, Loader2, X } from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import { TokenManager, API_BASE_URL } from '../../services/httpClient';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import type { UnifiedCitation } from '../../types/citation';
import { parsePiiMarkers } from '../../utils/parsePiiMarkers';
import { EntryActions } from '../ask/EntryActions';
import type { ContextItem } from '../../contexts/AskSidebarContext';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';

const CORRECTION_REASONS = [
  { id: 'additional_context', label: 'Additional context' },
  { id: 'inaccurate_extraction', label: 'Inaccurate extraction' },
  { id: 'missing_detail', label: 'Missing detail' },
  { id: 'factual_error', label: 'Factual error' },
  { id: 'tone_language', label: 'Tone / language' },
  { id: 'other', label: 'Other' },
];

interface EditableNarrativeSectionProps {
  caseId: string;
  /** Unique key to locate this section in the metadata (e.g. sectionId or 'caseSummary') */
  sectionKey: string;
  /** Current narrative text */
  narrative: string;
  /** Resolved citations for display mode */
  citations?: UnifiedCitation[];
  /** Callback to produce an updated metadata object with the new narrative */
  buildUpdatedMetadata: (newNarrative: string) => any;
  /** Section title rendered with an inline edit button */
  title: React.ReactNode;
  /** Icon element rendered before the title */
  icon?: React.ReactNode;
  /** Source label for the context basket (e.g. "Case Overview", "Claimant Background") */
  sourceLabel?: string;
}

/**
 * Wraps a narrative text block with in-situ editing: pencil icon next to the
 * title toggles a textarea, save persists the updated metadata JSONB to the backend.
 */
export const EditableNarrativeSection: React.FC<EditableNarrativeSectionProps> = ({
  caseId,
  sectionKey,
  narrative,
  citations = [],
  buildUpdatedMetadata,
  title,
  icon,
  sourceLabel,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const queryClient = useQueryClient();
  const { isEventMarked } = useKeyFindings();

  const sectionItemId = `section-${sectionKey}`;
  const isMarked = isEventMarked(sectionItemId);
  const titleText = typeof title === 'string' ? title : sectionKey.replace(/_/g, ' ');

  const contextItem: ContextItem = {
    id: sectionItemId,
    type: 'narrative-section' as ContextItem['type'],
    summary: narrative.replace(/\[\d+\]/g, '').slice(0, 120),
    source: sourceLabel || titleText,
    data: { sectionKey, narrative },
  };

  const saveMutation = useMutation(
    async (newNarrative: string) => {
      const updated = buildUpdatedMetadata(newNarrative);
      // The save endpoint returns plain text, not JSON — use fetch directly
      const token = TokenManager.getToken();
      const res = await fetch(`${API_BASE_URL}/intelligence/metadata/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ caseId, metadata: updated }),
      });
      if (!res.ok) throw new Error('Failed to save');
      return updated;
    },
    {
      onSuccess: (updated) => {
        queryClient.setQueryData(['metadata', caseId], updated);
        setEditing(false);
        setShowReasonModal(false);
        setSelectedReason(null);
        setCustomReason('');
      },
    }
  );

  const startEditing = () => {
    const clean = parsePiiMarkers(narrative).cleanText.replace(/\s*\[\d+\]/g, '');
    setDraft(clean);
    setEditing(true);
  };

  const handleSave = () => {
    if (draft.trim() === narrative.trim()) {
      setEditing(false);
      return;
    }
    setShowReasonModal(true);
  };

  const correctionReason = selectedReason
    ? CORRECTION_REASONS.find(r => r.id === selectedReason)!.label +
      (customReason.trim() ? `: ${customReason.trim()}` : '')
    : customReason.trim();

  const handleReasonSubmit = () => {
    saveMutation.mutate(draft);
  };

  return (
    <div>
      {/* Title row with inline pencil button and actions */}
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className="text-base font-semibold text-gray-900 dark:text-medical-100 flex items-center gap-2">
          {title}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (editing) {
              setEditing(false);
            } else {
              startEditing();
            }
          }}
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-medical-400 dark:text-medical-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
          title="Edit section"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <div className="ml-auto">
          <EntryActions
            compact
            item={contextItem}
            onCopy={() => narrative.replace(/\[\d+\]/g, '').trim()}
          />
        </div>
      </div>

      {/* Content: textarea when editing, rendered markdown otherwise */}
      {editing ? (
        <div onClick={(e) => e.stopPropagation()}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            className="w-full text-sm text-medical-900 dark:text-medical-100 leading-relaxed bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-primary-400 resize-y"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saveMutation.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={saveMutation.isLoading}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-medical-600 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
            >
              Cancel
            </button>
            {saveMutation.isError && (
              <span className="text-xs text-red-600 dark:text-red-400">Failed to save</span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-700 dark:text-medical-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none">
          <MarkdownWithCitations content={narrative} citations={citations} />
        </div>
      )}

      {/* Correction reason modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowReasonModal(false)}>
          <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
          <div
            className="relative w-full max-w-md mx-4 rounded-xl border border-medical-200 dark:border-medical-600 bg-white dark:bg-medical-800 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-medical-900 dark:text-medical-100">
                Why are you editing this section?
              </h3>
              <button
                onClick={() => setShowReasonModal(false)}
                className="p-1 rounded-md text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {CORRECTION_REASONS.map((reason) => (
                <button
                  key={reason.id}
                  type="button"
                  onClick={() => setSelectedReason((prev) => (prev === reason.id ? null : reason.id))}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                    selectedReason === reason.id
                      ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'border-medical-200 dark:border-medical-600 text-medical-600 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700'
                  }`}
                >
                  {reason.label}
                </button>
              ))}
            </div>

            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder={selectedReason ? 'Add details (optional)' : 'Or describe the reason...'}
              maxLength={500}
              rows={2}
              className="w-full rounded-lg border border-medical-200 dark:border-medical-600 bg-white dark:bg-medical-900 px-3 py-2 text-sm text-medical-900 dark:text-medical-100 placeholder-medical-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors resize-none"
            />

            {saveMutation.isError && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">Failed to save</p>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowReasonModal(false)}
                disabled={saveMutation.isLoading}
                className="px-3 py-1.5 rounded-md text-xs font-medium text-medical-600 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReasonSubmit}
                disabled={correctionReason.length === 0 || saveMutation.isLoading}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveMutation.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save correction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
